/**
 * features/finance/SiblingDiscounts.tsx
 * ------------------------------------------------------------------
 * Sibling concessions: find a pupil, say what they get.
 *
 * NOW THE RECORD OF EARLIER DISCOUNTS (Nov 2026). Discounts are issued from the
 * Discounts screen, as a receipt is, so this no longer records or applies them.
 * What it did stays on view, and an entry can still be ended. Below is how it
 * worked until then.
 *
 * ONE CONCESSION TYPE, ON PURPOSE. This screen grants SIBLING discounts and
 * nothing else. Staff children, bursaries and full-annual-payment discounts
 * are recorded in Fee Special Cases, and the Fees tab no longer sets any
 * discount at all.
 *
 * That separation is not tidiness. A bursary could previously be granted
 * here AND awarded in Special Cases, and the two reach the invoice by
 * different routes — Special Cases reduces the bill when it is raised, this
 * screen credits it afterwards — so the same award could be given twice with
 * nothing on either screen showing the other. Each concession type now has
 * exactly one home.
 *
 * REPLACES the automatic sibling discount, which needed a family, a
 * birth-order rank within it and an approval before a pula came off a bill —
 * and whose families had been built partly from surname, putting five
 * unrelated Molefes in one household. It had never once been used.
 *
 * The screen is a search box on purpose. A list of 826 pupils to scroll is
 * not how a bursar works: they are looking at a specific child because a
 * specific parent is standing in front of them.
 *
 * THE SCHOOL'S ORDER OF WORK, which this screen follows rather than fights:
 * the billing run raises every invoice at the FULL fee, and the bursar
 * discounts afterwards. So nothing here changes a bill by itself.
 *
 *   RECORD a concession — the register entry. Who gets what, and why.
 *   APPLY it to an invoice — raises a CREDIT NOTE with its own number and
 *   its own journal, posting to 4900 Discounts & Bursaries Granted.
 *
 * The two are separate buttons because they are separate events, and the
 * second is refused if that concession has already been credited against
 * that invoice. Applying by hand is applying twice waiting to happen.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BadgePercent, Search, Plus, Loader2, X, AlertCircle, CheckCircle2,
  Ban, ReceiptText,
} from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useData } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { PageHeader, SoftCard, StatChip, glassFieldClass, btnPrimary, btnGhost } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import {
  FINANCE_COLLECTIONS, FEE_CATEGORY_LABELS, round2,
  type Invoice, type FeeCategory,
} from '@/lib/financeCore';
import {
  DISCOUNT_MODE_LABELS, defaultLabel, toEngineDiscount, validateDiscount, alreadyApplied, isUnapplied,
  type StudentDiscount, type DiscountMode,
} from '@/lib/studentDiscounts';
import {
  recordAndApply, endStudentDiscount, fetchStudentDiscounts,
  previewAgainstInvoice, previewDetail, applyToInvoice, routeFor,
} from '@/features/finance/studentDiscountWrite';
import type { Student } from '@/types';

const money = (n: number) => `P${round2(n).toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StudentDiscounts() {
  const { students, classes } = useData();
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canEdit = perms.canManageFinanceConfig;
  /** Retired: see the top of this file. */
  const canRecord = false;

  const actor = useMemo(
    () => ({ uid: userData?.uid, name: userData?.displayName, email: userData?.email }),
    [userData],
  );

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Student | null>(null);
  const [discounts, setDiscounts] = useState<StudentDiscount[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // the form
  /* Fixed. See the header — every other concession type has its own home. */
  const kind: StudentDiscount['kind'] = 'sibling';
  const [mode, setMode] = useState<DiscountMode>('percent');
  const [value, setValue] = useState('');
  const [category, setCategory] = useState<FeeCategory | 'all'>('tuition');
  const [reason, setReason] = useState('');
  const [targetInvoiceId, setTargetInvoiceId] = useState('');

  const openInvoices = invoices.filter((i) => (i.balance ?? 0) > 0);
  const [saving, setSaving] = useState(false);
  const [ending, setEnding] = useState<StudentDiscount | null>(null);
  const [endReason, setEndReason] = useState('');
  const [crediting, setCrediting] = useState<{ d: StudentDiscount; inv: Invoice; because: string } | null>(null);

  const classNameOf = (id?: string) => classes.find((c) => c.id === id)?.name ?? '—';

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return students
      .filter((s) => s.status !== 'left' && (s.fullName || '').toLowerCase().includes(q))
      .slice(0, 12);
  }, [search, students]);

  async function openStudent(s: Student) {
    setSelected(s);
    setSearch('');
    setMsg(null);
    setLoading(true);
    try {
      const [ds, invSnap] = await Promise.all([
        fetchStudentDiscounts(s.id),
        getDocs(query(collection(db, FINANCE_COLLECTIONS.invoices), where('studentId', '==', s.id))),
      ]);
      setDiscounts(ds);
      setInvoices(
        invSnap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Invoice)
          .filter((i) => i.status !== 'void')
          .sort((a, b) => String(b.issuedAt).localeCompare(String(a.issuedAt))),
      );
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }

  const refresh = async () => { if (selected) await openStudent(selected); };

  /* WHY THE BUTTON WILL NOT GO, worked out as you type.
   *
   * This used to be checked only on click, and the message rendered at the
   * top of the page — well above the form. So pressing "Record discount"
   * with no reason typed did nothing visible at all: the explanation was
   * off-screen, and the bursar was left asking how to save. A control that
   * refuses has to say so where the person is looking. */
  const problem = validateDiscount({ mode, value: Number(value), reason })
    ?? (openInvoices.length === 0
      ? 'This pupil has no unpaid invoice for a discount to come off.'
      : null);

  /* The banner renders above the form, so neither a refusal nor a
     confirmation was visible from where the button is. Bring it into view. */
  const msgRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (msg) msgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [msg]);

  async function save() {
    if (!selected) return;
    if (problem) { setMsg({ ok: false, text: problem }); return; }
    setSaving(true);
    setMsg(null);
    try {
      const invoice = openInvoices.find((i) => i.id === targetInvoiceId) ?? openInvoices[0];
      if (!invoice) {
        setMsg({ ok: false, text: 'This pupil has no unpaid invoice to take a discount off.' });
        return;
      }
      /* ONE ACT. Recording used to leave the bill untouched until somebody
         came back and applied it — and three concessions worth P4,000 sat
         recorded and unapplied because the screen looked finished. */
      const r = await recordAndApply({
        studentId: selected.id,
        studentName: selected.fullName,
        kind, mode, value: Number(value), appliesToCategory: category, reason, actor,
      }, invoice, actor);
      setValue('');
      setReason('');
      setMsg({
        ok: true,
        text: r.route === 'discount'
          ? `${money(r.amount)} taken off ${invoice.invoiceNo ?? 'the invoice'} as a discount. `
            + 'The bill now shows the reduced fee.'
          : `${money(r.amount)} taken off ${invoice.invoiceNo ?? 'the invoice'} `
            + `as credit note ${r.adjustmentNo}. The bill is reduced now.`,
      });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function doEnd() {
    if (!ending) return;
    setSaving(true);
    try {
      await endStudentDiscount(ending, endReason, actor);
      setEnding(null);
      setEndReason('');
      setMsg({ ok: true, text: 'Ended. Invoices already raised under it keep their discount.' });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function doCredit() {
    if (!crediting) return;
    setSaving(true);
    try {
      const res = await applyToInvoice(crediting.d, crediting.inv, actor);
      setCrediting(null);
      setMsg({
        ok: true,
        text: res.route === 'discount'
          ? `${money(res.amount)} discounted off ${crediting.inv.invoiceNo ?? 'the invoice'}.`
          : `Credit note ${res.adjustmentNo} raised for ${money(res.amount)}.`,
      });
      await refresh();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const active = discounts.filter((d) => d.active);
  const ended = discounts.filter((d) => !d.active);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={BadgePercent}
        title="Sibling Discounts"
        subtitle="Sibling concessions, recorded against a named pupil"
      />

      <SoftCard>
        <p className="text-sm text-gray-700 dark:text-gray-200">
          New discounts are issued from the <span className="font-semibold">Discounts</span> screen, next to Payments.
          This page keeps the record of earlier ones.
        </p>
      </SoftCard>
      {!canEdit && (
        <SoftCard>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You can view concessions but not change them. Only the bursar and finance
            administrators may record or end one.
          </p>
        </SoftCard>
      )}

      {/* ---- find a pupil ---- */}
      <SoftCard>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5">
          Find a pupil
        </label>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Start typing a name…"
            className={glassFieldClass('pl-9')}
          />
        </div>
        {matches.length > 0 && (
          <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-xl divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden">
            {matches.map((s) => (
              <button
                key={s.id}
                onClick={() => openStudent(s)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/50 flex items-center gap-2"
              >
                <span className="font-medium text-gray-800 dark:text-gray-100">{s.fullName}</span>
                <span className="text-xs text-gray-400 ml-auto">{classNameOf(s.currentClass)}</span>
              </button>
            ))}
          </div>
        )}
      </SoftCard>

      {/* RECORDED AND NEVER APPLIED.
          Three of these were found on 5 September, worth P4,000: the bursar
          had filled the form in and nothing had come off any bill, because
          recording used to be a separate step from applying. Recording does
          both now, but the ones already on file are invisible unless said
          out loud. */}
      {selected && discounts.some((d) => isUnapplied(d)) && (
        <SoftCard>
          <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">
                {discounts.filter(isUnapplied).length} concession
                {discounts.filter(isUnapplied).length === 1 ? '' : 's'} on this pupil never reached a bill.
              </p>
              <p className="mt-0.5">
                They were recorded before a discount took effect on recording, so the parent still
                owes the full fee. Issue it from the Discounts screen, then end the entry here.
              </p>
            </div>
          </div>
        </SoftCard>
      )}

      {msg && (
        <div ref={msgRef} className={`rounded-xl px-4 py-3 text-sm flex items-start gap-2 ${
          msg.ok
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        }`}>
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {loading && (
        <SoftCard><div className="flex justify-center py-8"><Spinner label="Loading…" /></div></SoftCard>
      )}

      {selected && !loading && (
        <>
          <SoftCard>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-semibold text-gray-900 dark:text-white">{selected.fullName}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {classNameOf(selected.currentClass)}
                  {selected.admissionNumber ? ` · ${selected.admissionNumber}` : ''}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <StatChip tone="blue" value={String(active.length)} label="Active sibling discounts" />
                <StatChip tone="gold" value={String(openInvoices.length)} label="Unpaid invoices" />
              </div>
            </div>
          </SoftCard>

          {/* ---- record one ---- */}
          {canRecord && (
            <SoftCard>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3 flex items-center gap-2">
                <Plus size={15} /> Record a sibling discount
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">How</label>
                  <select value={mode} onChange={(e) => setMode(e.target.value as DiscountMode)} className={glassFieldClass()}>
                    {(Object.keys(DISCOUNT_MODE_LABELS) as DiscountMode[]).map((m) => (
                      <option key={m} value={m}>{DISCOUNT_MODE_LABELS[m]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    {mode === 'percent' ? 'Percentage' : 'Amount (P)'}
                  </label>
                  <input
                    type="number" min="0" step={mode === 'percent' ? '0.5' : '0.01'}
                    value={value} onChange={(e) => setValue(e.target.value)}
                    placeholder={mode === 'percent' ? 'e.g. 10' : 'e.g. 1500.00'}
                    className={glassFieldClass()}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Applies to</label>
                  <select value={category} onChange={(e) => setCategory(e.target.value as FeeCategory | 'all')} className={glassFieldClass()}>
                    <option value="tuition">{FEE_CATEGORY_LABELS.tuition} only</option>
                    <option value="all">The whole bill</option>
                    {(Object.keys(FEE_CATEGORY_LABELS) as FeeCategory[])
                      .filter((c) => c !== 'tuition')
                      .map((c) => <option key={c} value={c}>{FEE_CATEGORY_LABELS[c]} only</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Comes off <span className="text-gray-400">— which bill</span>
                  </label>
                  <select
                    value={targetInvoiceId || (openInvoices[0]?.id ?? '')}
                    onChange={(e) => setTargetInvoiceId(e.target.value)}
                    className={glassFieldClass()}
                  >
                    {openInvoices.length === 0 && <option value="">No unpaid invoice</option>}
                    {openInvoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoiceNo ?? inv.id} — {money(inv.balance ?? 0)} outstanding
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Reason <span className="text-gray-400">— the record of why the school gave this up</span>
                  </label>
                  <input
                    value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Second child at the College, agreed with the Principal"
                    className={glassFieldClass()}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <button onClick={save} disabled={saving || !!problem} className={btnPrimary}>
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Record and take it off the bill
                </button>
                {problem ? (
                  <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                    <AlertCircle size={13} className="shrink-0" /> {problem}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    A once-off reduction. It is credited to the chosen invoice straight away.
                  </p>
                )}
              </div>
            </SoftCard>
          )}

          {/* ---- what they have ---- */}
          <SoftCard>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
              Active sibling discounts
            </h3>
            {active.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                None. This pupil has no sibling discount.
              </p>
            ) : (
              <div className="space-y-2">
                {active.map((d) => (
                  <div key={d.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {defaultLabel(toEngineDiscount(d))}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {d.appliesToCategory === 'all'
                            ? 'the whole bill'
                            : `${FEE_CATEGORY_LABELS[d.appliesToCategory as FeeCategory] ?? d.appliesToCategory} only`}
                          {' · '}{d.reason}
                        </p>
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          Recorded {new Date(d.createdAt).toLocaleDateString()}
                          {d.createdByName ? ` by ${d.createdByName}` : ''}
                        </p>
                      </div>
                      {canEdit && (
                        <button onClick={() => setEnding(d)} className={btnGhost}>
                          <Ban size={13} /> End
                        </button>
                      )}
                    </div>

                    {/* Applying to an invoice already raised. Discount or credit
                        note depending on whether it has posted — routeFor decides. */}
                    {canRecord && openInvoices.length > 0 && (
                      <div className="mt-2.5 pt-2.5 border-t border-gray-100 dark:border-gray-700">
                        <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                          Apply to an invoice — the bill is reduced straight away
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {openInvoices.map((inv) => {
                            const { amount: amt, blockedReason } = previewDetail(d, inv);
                            const done = alreadyApplied(d, inv.id);
                            return (
                              <button
                                key={inv.id}
                                disabled={amt <= 0 || !!done}
                                onClick={async () => setCrediting({ d, inv, because: (await routeFor(inv)).because })}
                                title={
                                  done
                                    ? `Already credited ${done.adjustmentNo ? `as ${done.adjustmentNo}` : ''} on ${String(done.at).slice(0, 10)}`
                                    : blockedReason ?? undefined
                                }
                                className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
                              >
                                <ReceiptText size={12} className="inline mr-1" />
                                {inv.invoiceNo}
                                {(inv.lines?.length ?? 0) === 0 && (
                                  <span className="text-gray-400"> (b/f)</span>
                                )}
                                {' · '}
                                {done ? `credited ${money(done.amount)}` : amt > 0 ? money(amt) : '—'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {ended.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                  {ended.length} ended concession{ended.length === 1 ? '' : 's'}
                </summary>
                <div className="mt-2 space-y-1.5">
                  {ended.map((d) => (
                    <div key={d.id} className="text-xs text-gray-500 dark:text-gray-400">
                      <span className="line-through">{defaultLabel(toEngineDiscount(d))}</span>
                      {' — ended '}{d.endedAt ? new Date(d.endedAt).toLocaleDateString() : ''}
                      {d.endedReason ? `: ${d.endedReason}` : ''}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </SoftCard>
        </>
      )}

      {/* ---- end confirmation ---- */}
      {ending && (
        <Modal title="End this concession?" onClose={() => setEnding(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {defaultLabel(toEngineDiscount(ending))} stops being offered against new invoices.
            Reductions already applied under it stand, and so do the journals that posted them.
          </p>
          <input
            value={endReason}
            onChange={(e) => setEndReason(e.target.value)}
            placeholder="Reason for ending it"
            className={glassFieldClass('mt-3')}
          />
          <div className="flex gap-2 mt-4">
            <button onClick={doEnd} disabled={saving} className={btnPrimary}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />} End it
            </button>
            <button onClick={() => setEnding(null)} className={btnGhost}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ---- credit confirmation ---- */}
      {crediting && (
        <Modal title="Apply to this invoice?" onClose={() => setCrediting(null)}>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            This reduces <span className="font-medium">{crediting.inv.invoiceNo}</span> by{' '}
            <span className="font-semibold">{money(previewAgainstInvoice(crediting.d, crediting.inv))}</span>.
          </p>
          {/* Said before they commit, and worked out from the ledger rather
              than assumed — see discountRoute. */}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            {crediting.because} Either way it posts to Discounts &amp; Bursaries Granted, so the
            school's gross fee income stays intact.
          </p>
          <div className="flex gap-2 mt-4">
            <button onClick={doCredit} disabled={saving} className={btnPrimary}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <ReceiptText size={14} />} Apply
            </button>
            <button onClick={() => setCrediting(null)} className={btnGhost}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
